<template>
  <span :class="$style.numberTicker">
    <span v-if="!isPositive">-</span>
    <span :key="digit" v-for="digit in digits">
      {{ figures[digit - 1] }}
    </span>
  </span>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { defineComponent } from 'vue'

const rand10 = (): number => Math.floor(Math.random() * 10)
const FRAME_DURATION = 16.6 // 60fps => 1000 / 60

type Data = {
  number: number
  until: number
  figures: string[]
  timer: number | null
}

type Mode = 'random' | 'asc'

export default defineComponent({
  name: 'NumberTicker',
  props: {
    value: {
      type: Number,
      default: 0,
    },
    duration: {
      type: Number,
      default: 300,
    },
    mode: {
      type: String as PropType<Mode>,
      default: 'random',
    },
  },
  data(): Data {
    return {
      number: 0,
      until: new Date().getTime() + this.duration,
      figures: ['0'],
      timer: null,
    }
  },
  watch: {
    value: {
      handler() {
        if (this.timer !== null) {
          window.clearTimeout(this.timer)
        }
        this.invokeUpdateLoop()
      },
      immediate: true,
    },
  },
  methods: {
    invokeUpdateLoop() {
      if (this.mode !== 'random') {
        this.number = 0
      }
      this.until = new Date().getTime() + this.duration

      const updater =
        this.mode === 'random'
          ? this.randomUpdateFigures
          : this.ascendingUpdateFigures

      const isCompleted =
        this.mode === 'random'
          ? () => new Date().getTime() >= this.until
          : () => this.number >= this.value

      const update = () =>
        (this.timer = window.setTimeout(async () => {
          if (isCompleted()) {
            this.completeFigures()
            return
          }
          updater()
          await this.$nextTick()
          update()
        }, FRAME_DURATION))

      update()
    },
    ascendingUpdateFigures(): void {
      this.number += this.step
      this.figures = Array.from(this.number.toString())
    },
    randomUpdateFigures(): void {
      this.figures = Array.from({ length: this.digits }, () =>
        rand10().toString()
      )
    },
    completeFigures(): void {
      this.figures = Array.from(Math.abs(this.value).toString())
    },
  },
  computed: {
    step(): number {
      const step = Math.ceil(this.value / (this.duration / FRAME_DURATION))
      return Math.max(step, 1)
    },
    isPositive(): boolean {
      return this.value >= 0
    },
    digits(): number {
      const length = this.value.toString().length
      return this.isPositive ? length : length - 1
    },
  },
})
</script>

<style lang="scss" module>
.numberTicker {
  display: inline-flex;
  align-items: center;
}
</style>
